import {
  BookOpen,
  CalendarDays,
  Camera,
  Images,
  MapPin,
  Settings,
  ShoppingCart,
  SquarePen,
  Timer,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';
import type { Platform } from '@/contexts/PlatformContext';

export type FeatureShowcaseKey = string;

export type FeatureShowcaseItem = {
  key: FeatureShowcaseKey;
  icon: LucideIcon;
};

const IOS_FEATURES = [
  { key: 'scanner', icon: Camera },
  { key: 'library', icon: Images },
  { key: 'origin', icon: MapPin },
] as const satisfies ReadonlyArray<FeatureShowcaseItem>;

const MACOS_FEATURES = [
  { key: 'recipes', icon: BookOpen },
  { key: 'meals', icon: CalendarDays },
  { key: 'groceries', icon: ShoppingCart },
  { key: 'timers', icon: Timer },
  { key: 'planning', icon: UtensilsCrossed },
  { key: 'editable-recipes', icon: SquarePen },
] as const satisfies ReadonlyArray<FeatureShowcaseItem>;

export function featureShowcaseForPlatform(platform: Platform): ReadonlyArray<FeatureShowcaseItem> {
  return platform === 'ios' ? IOS_FEATURES : MACOS_FEATURES;
}

const IOS_DOC_FEATURES = [
  { key: 'scanner', icon: Camera },
  { key: 'library', icon: Images },
  { key: 'origin', icon: MapPin },
  { key: 'settings', icon: Settings },
] as const satisfies ReadonlyArray<FeatureShowcaseItem>;

const MACOS_DOC_FEATURES = [
  { key: 'recipes', icon: BookOpen },
  { key: 'meals', icon: CalendarDays },
  { key: 'groceries', icon: ShoppingCart },
  { key: 'settings', icon: Settings },
] as const satisfies ReadonlyArray<FeatureShowcaseItem>;

export function docsFeatureItemsForPlatform(platform: Platform): ReadonlyArray<FeatureShowcaseItem> {
  return platform === 'ios' ? IOS_DOC_FEATURES : MACOS_DOC_FEATURES;
}

export function featureShowcaseImageSrc(key: FeatureShowcaseKey, theme: 'light' | 'dark') {
  return `/feature-${key}-${theme}.png`;
}

function probeImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = src;
  });
}

export async function probeFeatureShowcaseImages(key: FeatureShowcaseKey) {
  const lightSrc = featureShowcaseImageSrc(key, 'light');
  const darkSrc = featureShowcaseImageSrc(key, 'dark');
  const [hasLight, hasDark] = await Promise.all([probeImage(lightSrc), probeImage(darkSrc)]);

  return { hasLight, hasDark, lightSrc, darkSrc };
}
